/**
 * Copyright (c) 2023 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { getAppConfig } from './env.dynamic.client'

export const calcBirthDate = (age: number) => {
  const birthDate = new Date()
  birthDate.setFullYear(birthDate.getFullYear() - age)
  return birthDate
}

/**
 * Convert a enum name to a string with each word capitalized.
 * @param {*} str Enum name
 * @returns String representation
 */

export function enumToString(str: string, joinChar = ' '): string {
  if (str) {
    const words = str.toLowerCase().split('_')

    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1)
    }

    return words.join(joinChar)
  }
  return str
}

export const heightString = (cm: number): string => {
  const totalInches = cm * 0.3937
  const feet = Math.floor(totalInches / 12)
  const inches = Math.floor((totalInches / 12 - feet) * 12)
  return `${cm}cm / ${feet}' ${inches}"`
}

export const validateDateOfBirth = (value) => {
  const selectedDate = new Date(value)
  if (Number.isNaN(selectedDate.getTime())) {
    return 'Please enter a valid date'
  }
  const selected = selectedDate.getFullYear()
  const now = new Date().getFullYear()
  return now - selected >= 18 || 'You must be 18 years old to be a member of this site.'
}

export const onlineNow = (dateString: string | Date | undefined): boolean => {
  if (!dateString) return false

  const dateTime = typeof dateString === 'string' ? Date.parse(dateString) : dateString.getTime()
  const lastOnlineMinutes = (Date.now() - dateTime) / (1000 * 60)
  return lastOnlineMinutes < 5
}

export const videoDurationStr = (durationSec?: number): string => {
  if (durationSec) {
    const dateObj = new Date(durationSec * 1000)
    const hours = dateObj.getUTCHours()
    const minutes = dateObj.getUTCMinutes()
    const seconds = dateObj.getSeconds()
    const timeString =
      hours.toString().padStart(2, '0') +
      ':' +
      minutes.toString().padStart(2, '0') +
      ':' +
      seconds.toString().padStart(2, '0')
    return timeString
  }

  return ''
}

export function baseUrl() {
  const appConfig = getAppConfig()
  const baseAddress = appConfig.APP_URL

  if (!baseAddress) {
    console.error('APP_URL variable was not set')
  }

  return baseAddress
}

const TRAILING_SLASH_REGEX = /\/$/

/** Absolute document URL for a pathname, using `APP_URL` as origin (e.g. canonical / breadcrumb links). */
export function absoluteUrlForPath(pathname: string): string {
  const root = baseUrl().replace(TRAILING_SLASH_REGEX, '')
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`
  return path === '/' ? `${root}/` : `${root}${path}`
}

export const profileAgeRange = ({ profile }) => {
  if (!profile) {
    return ''
  }

  if (profile.minAge === profile.maxAge) {
    return profile.minAge
  }

  return profile.minAge + ' - ' + profile.maxAge
}

// Create a list of timezone options using Intl API
export const getTimezoneOptions = () => {
  try {
    // Define common timezones by region
    const commonTimezones = [
      // North America
      'America/New_York', // Eastern Time
      'America/Chicago', // Central Time
      'America/Denver', // Mountain Time
      'America/Los_Angeles', // Pacific Time
      'America/Phoenix', // Arizona (no DST)
      'America/Anchorage', // Alaska
      'Pacific/Honolulu', // Hawaii

      // Canada
      'America/Toronto', // Eastern Canada
      'America/Vancouver', // Pacific Canada
      'America/Edmonton', // Mountain Canada
      'America/Winnipeg', // Central Canada
      'America/Halifax', // Atlantic Canada
      'America/St_Johns', // Newfoundland

      // Europe
      'Europe/London', // UK
      'Europe/Paris', // Central Europe
      'Europe/Berlin', // Germany
      'Europe/Rome', // Italy
      'Europe/Madrid', // Spain
      'Europe/Amsterdam', // Netherlands
      'Europe/Brussels', // Belgium
      'Europe/Zurich', // Switzerland
      'Europe/Stockholm', // Sweden
      'Europe/Oslo', // Norway
      'Europe/Copenhagen', // Denmark
      'Europe/Dublin', // Ireland
      'Europe/Athens', // Greece
      'Europe/Istanbul', // Turkey
      'Europe/Moscow', // Russia

      // Asia/Pacific
      'Asia/Tokyo', // Japan
      'Asia/Shanghai', // China
      'Asia/Singapore', // Singapore
      'Asia/Hong_Kong', // Hong Kong
      'Asia/Seoul', // Korea
      'Asia/Kolkata', // India
      'Asia/Dubai', // UAE
      'Asia/Bangkok', // Thailand
      'Asia/Manila', // Philippines
      'Australia/Sydney', // Eastern Australia
      'Australia/Perth', // Western Australia
      'Australia/Melbourne', // Victoria
      'Australia/Brisbane', // Queensland
      'Pacific/Auckland', // New Zealand

      // South/Central America
      'America/Mexico_City', // Mexico
      'America/Sao_Paulo', // Brazil
      'America/Argentina/Buenos_Aires', // Argentina
      'America/Bogota', // Colombia
      'America/Lima', // Peru
      'America/Santiago', // Chile
    ]

    return commonTimezones
      .map((tz) => {
        // Get the timezone offset
        const date = new Date()

        // Get timezone abbreviation
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          timeZoneName: 'short',
        })
        const parts = formatter.formatToParts(date)
        const timeZonePart = parts.find((part) => part.type === 'timeZoneName')
        const timeZoneAbbr = timeZonePart ? timeZonePart.value : ''

        // Calculate the UTC offset directly
        const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
        const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }))
        const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60_000
        const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
        const offsetMins = Math.abs(offsetMinutes) % 60

        const sign = offsetMinutes >= 0 ? '+' : '-'
        const offset = `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`

        return {
          label: `(UTC${offset}) ${tz} ${timeZoneAbbr}`,
          value: tz,
        }
      })
      .sort((a, b) => {
        // Sort by offset first (extract the UTC+/-XX:XX part)
        const offsetA = a.label.substring(4, 10)
        const offsetB = b.label.substring(4, 10)

        if (offsetA < offsetB) return -1
        if (offsetA > offsetB) return 1

        // If offsets are the same, sort alphabetically by timezone name
        return a.label.localeCompare(b.label)
      })
  } catch (error) {
    // Fallback for browsers that don't support Intl.supportedValuesOf
    console.error('Timezone API not supported:', error)
    return [
      // North American Timezones
      { label: 'America/New_York (EST/EDT -05:00/-04:00)', value: 'America/New_York' },
      { label: 'America/Chicago (CST/CDT -06:00/-05:00)', value: 'America/Chicago' },
      { label: 'America/Denver (MST/MDT -07:00/-06:00)', value: 'America/Denver' },
      { label: 'America/Los_Angeles (PST/PDT -08:00/-07:00)', value: 'America/Los_Angeles' },
      { label: 'America/Anchorage (AKST/AKDT -09:00/-08:00)', value: 'America/Anchorage' },
      { label: 'America/Adak (HST/HDT -10:00/-09:00)', value: 'America/Adak' },
      { label: 'Pacific/Honolulu (HST -10:00)', value: 'Pacific/Honolulu' },
      { label: 'America/Phoenix (MST -07:00)', value: 'America/Phoenix' },
      { label: 'America/Toronto (EST/EDT -05:00/-04:00)', value: 'America/Toronto' },
      { label: 'America/Vancouver (PST/PDT -08:00/-07:00)', value: 'America/Vancouver' },
      { label: 'America/Edmonton (MST/MDT -07:00/-06:00)', value: 'America/Edmonton' },
      { label: 'America/Winnipeg (CST/CDT -06:00/-05:00)', value: 'America/Winnipeg' },
      { label: 'America/Halifax (AST/ADT -04:00/-03:00)', value: 'America/Halifax' },
      { label: 'America/St_Johns (NST/NDT -03:30/-02:30)', value: 'America/St_Johns' },
      { label: 'America/Mexico_City (CST/CDT -06:00/-05:00)', value: 'America/Mexico_City' },
      // European Timezones
      { label: 'Europe/London (GMT/BST +00:00/+01:00)', value: 'Europe/London' },
      { label: 'Europe/Paris (CET/CEST +01:00/+02:00)', value: 'Europe/Paris' },
      { label: 'Europe/Berlin (CET/CEST +01:00/+02:00)', value: 'Europe/Berlin' },
      // Asia/Pacific Timezones
      { label: 'Asia/Tokyo (JST +09:00)', value: 'Asia/Tokyo' },
      { label: 'Asia/Shanghai (CST +08:00)', value: 'Asia/Shanghai' },
      { label: 'Australia/Sydney (AEST/AEDT +10:00/+11:00)', value: 'Australia/Sydney' },
    ]
  }
}

/**
 * Get the abbreviation for a given timezone
 * @param timezone The timezone string (e.g., 'America/New_York')
 * @returns The timezone abbreviation (e.g., 'EST' or 'EDT')
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  try {
    // Create a formatter that includes the timezone abbreviation
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })

    // Format the current date and extract the timezone part
    const parts = formatter.formatToParts(new Date())
    const timeZonePart = parts.find((part) => part.type === 'timeZoneName')

    // Return the abbreviation or an empty string if not found
    return timeZonePart ? timeZonePart.value : ''
  } catch (error) {
    console.error(`Error getting timezone abbreviation for ${timezone}:`, error)
    return ''
  }
}

/**
 * Determines if a given timezone matches the current user's timezone.
 * This function handles cases where timezone names differ but offsets are identical.
 *
 * @param timezone The timezone string to check (e.g., 'America/New_York')
 * @returns boolean indicating if the timezone matches the current timezone
 */
export const isCurrentTimezone = (timezone: string): boolean => {
  if (!timezone) return true

  try {
    // Get the current timezone from the browser
    const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    // First check: direct name match
    if (currentTimezone === timezone) return true

    // Second check: compare offsets
    const now = new Date()

    // Get offset for the provided timezone
    const tzOffset = new Date(now.toLocaleString('en-US', { timeZone: timezone })).getTime()

    // Get offset for the current timezone
    const currentOffset = new Date(now.toLocaleString('en-US', { timeZone: currentTimezone })).getTime()

    // If the offsets are the same, the timezones are equivalent
    return tzOffset === currentOffset
  } catch (error) {
    console.error('Error comparing timezones:', error)
    return false
  }
}

export const limitBadgeNumber = (count: number, limit = 99) => {
  return count > limit ? `${limit}+` : count.toString()
}
